/**
 * BulkAnalyzeTab — Elite feature. Upload a CSV or paste up to 100
 * addresses, get them scored against the user's active strategy, and
 * see them ranked in a results table.
 *
 * Poll the backend for progress every 2s until the job finishes.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileText, Play, RefreshCw, Crown, Lock, Trash2, Sparkles,
  CheckCircle2, XCircle, ChevronDown, Download, Info, Briefcase, ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { API, formatCurrency } from "../../lib/helpers";
import StrategyPicker from "../StrategyPicker";

const MAX_ROWS = 100;
const TOP_N_PRESELECT = 10;
const CRITERIA_MIN_SCORE = 60;  // "meets criteria" threshold

export default function BulkAnalyzeTab({ subscription, onUpgrade }) {
  const isElite = !!subscription?.is_elite;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [pasted, setPasted] = useState("");
  const [strategyId, setStrategyId] = useState("balanced");
  const [strategyWeights, setStrategyWeights] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [pastJobs, setPastJobs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const pollRef = useRef(null);

  const loadPastJobs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bulk/jobs`);
      setPastJobs(r.data.jobs || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (isElite) loadPastJobs(); }, [isElite, loadPastJobs]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/bulk/jobs/${jobId}`);
        setActiveJob(r.data);
        if (r.data.status === "completed" || r.data.status === "failed" || r.data.status === "interrupted") {
          stopPolling();
          loadPastJobs();
        }
      } catch (err) {
        console.debug("[bulk] poll failed:", err);
        stopPolling();
      }
    }, 2000);
  }, [loadPastJobs]);

  useEffect(() => () => stopPolling(), []);

  // Memoized results + selection helpers must live BEFORE the paywall
  // early return so hook order stays stable across renders.
  const sortedResults = useMemo(() => {
    if (!activeJob?.results) return [];
    return [...activeJob.results].sort((a, b) => (b.deal_score || 0) - (a.deal_score || 0));
  }, [activeJob]);

  const preselectedForJobRef = useRef(null);
  useEffect(() => {
    if (!activeJob || activeJob.status !== "completed") return;
    if (preselectedForJobRef.current === activeJob.job_id) return;
    const winners = sortedResults
      .filter(r => r.status === "ok" && (r.deal_score || 0) >= CRITERIA_MIN_SCORE)
      .slice(0, TOP_N_PRESELECT)
      .map(r => r.address);
    setSelected(new Set(winners));
    preselectedForJobRef.current = activeJob.job_id;
  }, [activeJob, sortedResults]);

  const toggleRow = (address) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(address)) next.delete(address); else next.add(address);
      return next;
    });
  };

  const toggleAll = () => {
    const okAddresses = sortedResults.filter(r => r.status === "ok").map(r => r.address);
    setSelected(prev => prev.size === okAddresses.length ? new Set() : new Set(okAddresses));
  };

  const savePortfolio = async () => {
    if (!activeJob?.job_id || selected.size === 0) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/portfolio/bulk-from-job`, {
        job_id: activeJob.job_id,
        addresses: [...selected],
      });
      const imported = r.data.imported_count || 0;
      const skipped = r.data.skipped_count || 0;
      if (imported > 0) {
        toast.success(
          skipped > 0
            ? `Saved ${imported} to Portfolio · ${skipped} skipped (already there)`
            : `Saved ${imported} to Portfolio`
        );
      } else {
        toast.info(`${skipped} already in Portfolio — nothing new to save`);
      }
    } catch (err) {
      if (err.response?.status === 402) onUpgrade?.("elite:portfolio");
      else toast.error(err.response?.data?.detail || "Failed to save to Portfolio");
    } finally {
      setSaving(false);
    }
  };

  const kickoffJson = async () => {
    if (!isElite) { onUpgrade?.("elite:bulk_analyze"); return; }
    const addresses = pasted.split("\n").map(l => l.trim()).filter(Boolean);
    if (addresses.length === 0) { toast.error("Add at least one address"); return; }
    if (addresses.length > MAX_ROWS) { toast.error(`Max ${MAX_ROWS} addresses per job`); return; }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/bulk/analyze`, {
        addresses,
        strategy_id: strategyId,
        weights: strategyWeights,
      });
      setActiveJob(r.data);
      pollJob(r.data.job_id);
      toast.success(`Analyzing ${addresses.length} propert${addresses.length === 1 ? "y" : "ies"}…`);
      setPasted("");
    } catch (err) {
      if (err.response?.status === 402) onUpgrade?.("elite:bulk_analyze");
      else toast.error(err.response?.data?.detail || "Failed to start job");
    } finally {
      setStarting(false);
    }
  };

  const kickoffCsv = async (file) => {
    if (!isElite) { onUpgrade?.("elite:bulk_analyze"); return; }
    if (!file) return;
    setStarting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const r = await axios.post(`${API}/bulk/analyze/csv?strategy_id=${encodeURIComponent(strategyId)}`, formData);
      setActiveJob(r.data);
      pollJob(r.data.job_id);
      toast.success(`Analyzing ${r.data.addresses?.length || 0} properties from CSV…`);
    } catch (err) {
      if (err.response?.status === 402) onUpgrade?.("elite:bulk_analyze");
      else toast.error(err.response?.data?.detail || "Failed to upload CSV");
    } finally {
      setStarting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Delete this bulk analysis job?")) return;
    try {
      await axios.delete(`${API}/bulk/jobs/${jobId}`);
      if (activeJob?.job_id === jobId) setActiveJob(null);
      loadPastJobs();
    } catch { toast.error("Failed to delete"); }
  };

  const openJob = async (jobId) => {
    try {
      const r = await axios.get(`${API}/bulk/jobs/${jobId}`);
      setActiveJob(r.data);
      // Fire the "opened" event once per session per job.
      axios.post(`${API}/analytics/track`, { event_type: "bulk_result_opened", metadata: { job_id: jobId } })
        .catch((err) => console.debug("[bulk] track failed:", err));
    } catch { toast.error("Failed to load job"); }
  };

  const downloadResults = () => {
    if (!activeJob?.results?.length) return;
    const cols = ["address", "status", "provider", "price", "bedrooms", "bathrooms", "sqft", "monthly_rent", "cash_flow_monthly", "cap_rate", "cash_on_cash", "deal_score"];
    const csv = [
      cols.join(","),
      ...activeJob.results.map(r => cols.map(c => JSON.stringify(r[c] ?? "")).join(","))
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bulk-${activeJob.job_id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Elite paywall ─────────────────────────────────────────────────────
  if (!isElite) {
    return (
      <div className="max-w-3xl mx-auto py-8" data-testid="bulk-elite-paywall">
        <Card className="relative overflow-hidden border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-emerald-500/5">
          <div className="absolute top-0 left-0 right-0 bg-amber-500 text-black text-center text-xs font-bold py-1 uppercase tracking-wider">
            <Crown className="w-3 h-3 inline mr-1 -mt-0.5" /> Elite feature · Bulk Analyze
          </div>
          <CardContent className="pt-12 pb-8 px-6 sm:px-10">
            <div className="text-center mb-6">
              <div className="inline-flex w-14 h-14 rounded-2xl bg-amber-500/20 items-center justify-center mb-3">
                <Upload className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Score 100 properties in minutes
              </h3>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto">
                Upload a CSV from your wholesaler, MLS export, or driving-for-dollars
                list. RealixData analyzes every row against <b>your</b> investment
                strategy and ranks the winners.
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 mb-6 font-mono text-[11px] overflow-hidden">
              <div className="text-emerald-500 mb-1">▸ Analyzing 37 properties…</div>
              <div className="grid grid-cols-4 gap-2 text-[10px] text-muted-foreground mb-1">
                <span>SCORE</span><span>ADDRESS</span><span>PRICE</span><span>CASH FLOW</span>
              </div>
              {[
                ["94", "123 Main St", "$315K", "+$842"],
                ["91", "456 Oak Ave", "$289K", "+$691"],
                ["86", "789 Pine St", "$340K", "+$522"],
                ["…", "…", "…", "…"],
                ["42", "321 Bay Dr", "$410K", "-$218"],
              ].map((row) => (
                <div key={row.join("|")} className="grid grid-cols-4 gap-2 text-foreground py-0.5">
                  <span className="text-amber-500 font-bold">{row[0]}</span>
                  <span>{row[1]}</span><span>{row[2]}</span>
                  <span className={row[3].startsWith("+") ? "text-emerald-500" : "text-red-500"}>{row[3]}</span>
                </div>
              ))}
              <div className="text-emerald-500 mt-2 border-t border-border pt-2">
                ▸ 5 of 37 properties meet your criteria
              </div>
            </div>

            <div className="space-y-2.5 mb-7 max-w-md mx-auto">
              {[
                "CSV upload or paste up to 100 addresses",
                "Scores every property against your custom strategy",
                "Isolates failures — one bad row doesn't kill the job",
                "Live progress + downloadable CSV results",
                "Save top matches straight to your Portfolio (coming soon)",
              ].map(f => (
                <div key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 shadow-lg shadow-amber-500/30"
                onClick={() => navigate("/pricing")}
                data-testid="bulk-upgrade-cta"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Elite · $39/mo
              </Button>
              <span className="text-xs text-muted-foreground">
                Cancel anytime · 30-day money-back guarantee
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Elite user view ───────────────────────────────────────────────────
  const progress = activeJob?.progress;
  const pct = progress?.total ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="bulk-analyze-tab">
      {!activeJob && (
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" /> Bulk Analyze
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Upload a CSV or paste addresses. We&apos;ll score each one against your active strategy.
                </p>
              </div>
              <StrategyPicker
                value={strategyId}
                isElite
                onChange={(id, w) => { setStrategyId(id); setStrategyWeights(w); }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Paste addresses */}
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Paste addresses (one per line)
                </p>
                <Textarea
                  value={pasted}
                  onChange={e => setPasted(e.target.value)}
                  rows={7}
                  placeholder="123 Main St, Tampa, FL 33602&#10;456 Oak Ave, Tampa, FL 33603&#10;789 Pine St, Tampa, FL 33604"
                  className="font-mono text-xs"
                  data-testid="bulk-paste-textarea"
                />
                <Button
                  className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                  onClick={kickoffJson}
                  disabled={starting || !pasted.trim()}
                  data-testid="bulk-start-paste-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {starting ? "Starting…" : "Analyze"}
                </Button>
              </div>

              {/* CSV upload */}
              <div>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Upload CSV
                </p>
                <label
                  htmlFor="bulk-csv-file"
                  className="flex flex-col items-center justify-center gap-2 h-[176px] border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-amber-500/40 hover:bg-secondary/30 transition-colors"
                  data-testid="bulk-csv-dropzone"
                >
                  <FileText className="w-8 h-8 text-muted-foreground/60" />
                  <div className="text-center">
                    <p className="text-sm font-medium">Click to select a CSV</p>
                    <p className="text-[11px] text-muted-foreground">
                      Columns: address, city, state, zip
                    </p>
                  </div>
                </label>
                <input
                  id="bulk-csv-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={e => kickoffCsv(e.target.files?.[0])}
                  data-testid="bulk-csv-file-input"
                />
                <p className="text-[10px] text-muted-foreground/70 mt-2 text-center">
                  Max {MAX_ROWS} rows per job
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active job progress + results */}
      {activeJob && (
        <Card className="border-border" data-testid={`bulk-job-${activeJob.job_id}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  {activeJob.status === "completed" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : activeJob.status === "interrupted" || activeJob.status === "failed" ? (
                    <XCircle className="w-4 h-4 text-red-500" />
                  ) : (
                    <RefreshCw className="w-4 h-4 text-amber-500 animate-spin" />
                  )}
                  Bulk Job · {activeJob.status === "interrupted" ? "interrupted (server restarted)" : activeJob.status}
                </h3>
                <p className="text-[11px] text-muted-foreground">
                  Strategy: <span className="text-foreground">{activeJob.strategy_id}</span> ·
                  Started {new Date(activeJob.created_at).toLocaleTimeString()}
                </p>
                {activeJob.status === "interrupted" && (
                  <p className="text-[11px] text-red-500 mt-1">
                    This job was mid-run when the server restarted. Any results below are the partial output — delete and re-run to finish it.
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {activeJob.status === "completed" && (
                  <Button size="sm" variant="outline" onClick={downloadResults} data-testid="bulk-download-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> CSV
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setActiveJob(null)} data-testid="bulk-close-btn">
                  New Job
                </Button>
              </div>
            </div>

            {/* Progress bar */}
            {progress && (
              <div className="mb-4">
                <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                  <span>{progress.processed}/{progress.total} processed</span>
                  <span>
                    <span className="text-emerald-500">{progress.ok} ok</span>
                    {progress.failed > 0 && <> · <span className="text-red-500">{progress.failed} failed</span></>}
                  </span>
                </div>
                <div className="h-2 bg-secondary/40 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Results table */}
            {sortedResults.length > 0 && (
              <>
                {/* Bulk action bar — visible once results exist. */}
                {activeJob.status === "completed" && (
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap" data-testid="bulk-action-bar">
                    <div className="text-[11px] text-muted-foreground">
                      {selected.size > 0 ? (
                        <>
                          <span className="text-foreground font-medium">{selected.size} selected</span>
                          {" · "}
                          {sortedResults.filter(r => r.status === "ok" && (r.deal_score || 0) >= CRITERIA_MIN_SCORE).length} meet criteria (score ≥ {CRITERIA_MIN_SCORE})
                        </>
                      ) : (
                        <>Select properties to save to your Portfolio</>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={savePortfolio}
                      disabled={selected.size === 0 || saving}
                      className="bg-amber-500 hover:bg-amber-600 text-black font-bold h-7 text-xs"
                      data-testid="bulk-save-selected-btn"
                    >
                      <Briefcase className="w-3.5 h-3.5 mr-1" />
                      {saving ? "Saving…" : `Save Selected to Portfolio (${selected.size})`}
                    </Button>
                  </div>
                )}

                <div className="rounded-lg border border-border overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-secondary/40 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  <div className="col-span-1 flex items-center">
                    {activeJob.status === "completed" && (
                      <button
                        onClick={toggleAll}
                        className="text-muted-foreground hover:text-foreground"
                        title="Toggle all"
                        data-testid="bulk-toggle-all"
                      >
                        <Checkbox
                          checked={selected.size > 0 && selected.size === sortedResults.filter(r => r.status === "ok").length}
                          onCheckedChange={toggleAll}
                        />
                      </button>
                    )}
                  </div>
                  <div className="col-span-1 text-right">Score</div>
                  <div className="col-span-4">Address</div>
                  <div className="col-span-2 text-right">Price</div>
                  <div className="col-span-2 text-right">Cash Flow</div>
                  <div className="col-span-1 text-right">Cap</div>
                  <div className="col-span-1 text-right">CoC</div>
                </div>
                {sortedResults.map((r, i) => {
                  const isSelected = selected.has(r.address);
                  const canSelect = r.status === "ok" && activeJob.status === "completed";
                  return (
                    <div
                      key={`${r.address}-${i}`}
                      className={`grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border items-center transition-colors ${r.status !== "ok" ? "bg-red-500/5" : isSelected ? "bg-amber-500/5" : ""}`}
                      data-testid={`bulk-result-row-${i}`}
                    >
                      <div className="col-span-1 flex items-center">
                        {canSelect ? (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRow(r.address)}
                            data-testid={`bulk-row-checkbox-${i}`}
                          />
                        ) : (
                          <span className="font-mono text-muted-foreground text-[10px]">#{i + 1}</span>
                        )}
                      </div>
                      <div className={`col-span-1 text-right font-mono font-bold ${(r.deal_score || 0) >= 75 ? "text-emerald-500" : (r.deal_score || 0) >= 50 ? "text-amber-500" : "text-red-500"}`}>
                        {r.status === "ok" ? r.deal_score : "—"}
                      </div>
                      <div className="col-span-4 truncate">
                        {r.status === "ok" ? (
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{r.address}</span>
                            {r.provider && (
                              <span
                                className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground font-mono flex-shrink-0"
                                title={`Data source: ${r.provider}`}
                                data-testid={`bulk-row-provider-${i}`}
                              >
                                {r.provider}
                              </span>
                            )}
                            {r.listing_url && (
                              <a
                                href={r.listing_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-blue-500 hover:text-blue-400"
                                title="View on Zillow"
                                data-testid={`bulk-row-zillow-link-${i}`}
                              >
                                Zillow<ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1">
                            <XCircle className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{r.address}</span>
                            <span className="text-[10px] text-muted-foreground ml-1">({r.reason || r.status})</span>
                          </span>
                        )}
                      </div>
                      <div className="col-span-2 text-right font-mono">{r.price ? formatCurrency(r.price) : "—"}</div>
                      <div className={`col-span-2 text-right font-mono ${(r.cash_flow_monthly || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        {r.cash_flow_monthly != null ? formatCurrency(r.cash_flow_monthly) : "—"}
                      </div>
                      <div className="col-span-1 text-right font-mono">{r.cap_rate != null ? `${r.cap_rate.toFixed(1)}%` : "—"}</div>
                      <div className="col-span-1 text-right font-mono">{r.cash_on_cash != null ? `${r.cash_on_cash.toFixed(1)}%` : "—"}</div>
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent jobs */}
      {pastJobs.length > 0 && !activeJob && (
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Recent Jobs
            </p>
            <div className="space-y-1.5">
              {pastJobs.slice(0, 5).map(j => (
                <div key={j.job_id} className="flex items-center gap-3 rounded-md border border-border p-2 hover:bg-secondary/30 transition-colors">
                  <div className="flex-shrink-0">
                    {j.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : j.status === "failed" || j.status === "interrupted" ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-amber-500 animate-spin" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">
                      {j.progress?.total || 0} propert{(j.progress?.total || 0) === 1 ? "y" : "ies"} · {j.strategy_id}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(j.created_at).toLocaleString()} · {j.progress?.ok || 0} ok · {j.progress?.failed || 0} failed
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => openJob(j.job_id)} className="h-7 text-xs" data-testid={`bulk-open-${j.job_id}`}>
                    Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteJob(j.job_id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600" data-testid={`bulk-delete-${j.job_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
